import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, Shirt, CalendarDays, Settings, LogOut, Loader2, Save, Instagram, ExternalLink, Image, Truck, Ruler } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import AdminSidebar from "../components/admin/AdminSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Social media icons as SVG components
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const FacebookIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/>
  </svg>
);

const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function AdminSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    instagram_url: "",
    tiktok_url: "",
    facebook_url: "",
    pinterest_url: "",
    whatsapp_url: ""
  });

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) { navigate("/admin/login"); return; }
    verifyAndFetch(token);
  }, [navigate]);

  const verifyAndFetch = async (token) => {
    try {
      await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      fetchSettings();
    } catch (error) {
      localStorage.removeItem("adminToken");
      navigate("/admin/login");
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/social-links`);
      setFormData({
        instagram_url: response.data.instagram_url || "",
        tiktok_url: response.data.tiktok_url || "",
        facebook_url: response.data.facebook_url || "",
        pinterest_url: response.data.pinterest_url || "",
        whatsapp_url: response.data.whatsapp_url || ""
      });
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    // Basic URL validation
    const urlPattern = /^https?:\/\/[^\s/$.?#].[^\s]*$|^$/;
    for (const [key, value] of Object.entries(formData)) {
      if (value && !urlPattern.test(value)) {
        toast.error(`Invalid URL format for ${key.replace('_url', '').replace('_', ' ')}`);
        return;
      }
    }
    
    try {
      setSaving(true);
      await axios.put(`${API}/settings/social-links`, formData, getAuthHeaders());
      toast.success("Social links saved successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAF8F0] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#9A7B4F]" />
    </div>
  );

  const socialFields = [
    { key: "instagram_url", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/mahra.insta" },
    { key: "tiktok_url", label: "TikTok", icon: TikTokIcon, placeholder: "https://tiktok.com/@mahra" },
    { key: "facebook_url", label: "Facebook", icon: FacebookIcon, placeholder: "https://facebook.com/mahra" },
    { key: "pinterest_url", label: "Pinterest", icon: PinterestIcon, placeholder: "https://pinterest.com/mahra" },
    { key: "whatsapp_url", label: "WhatsApp", icon: WhatsAppIcon, placeholder: "https://wa.me/61400000000" }
  ];

  return (
    <div className="min-h-screen bg-[#FAF8F0] flex">
      {/* Sidebar */}
      <AdminSidebar />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 md:p-10">
        <div className="max-w-2xl">
          <div className="mb-8">
            <h1 className="text-2xl text-[#676664] font-light mb-2">Settings</h1>
            <p className="text-sm text-[#676664]/60">Manage your website settings and social media links</p>
          </div>

          <form onSubmit={handleSave} className="space-y-8">
            {/* Social Media Links */}
            <div className="bg-white p-6 border border-[#282828]/10">
              <h2 className="text-sm text-[#676664] font-medium mb-6 tracking-wide">Social Media Links</h2>
              <p className="text-[11px] text-[#676664]/50 mb-6">
                Add your social media profile URLs. Leave empty to hide the icon from the website.
              </p>
              
              <div className="space-y-5">
                {socialFields.map(({ key, label, icon: Icon, placeholder }) => (
                  <div key={key} className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#9A7B4F]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#9A7B4F]" />
                    </div>
                    <div className="flex-1">
                      <Label className="text-[#676664]/60 text-[11px] mb-1 block">{label}</Label>
                      <Input
                        type="url"
                        value={formData[key]}
                        onChange={(e) => setFormData(p => ({ ...p, [key]: e.target.value }))}
                        placeholder={placeholder}
                        className="border-[#282828]/10"
                        data-testid={`${key}-input`}
                      />
                    </div>
                    {formData[key] && (
                      <a 
                        href={formData[key]} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="p-2 text-[#9A7B4F] hover:bg-[#9A7B4F]/10 transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <Button 
              type="submit" 
              disabled={saving}
              className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white px-8"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save Changes</>
              )}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
